<template>
  <div class="landing-wrapper min-h-screen w-full">

    <!-- ── HERO & PORTAL SELECTION ── -->
    <section class="relative flex flex-col items-center justify-center min-h-screen px-6 sm:px-8 text-center overflow-hidden">
      <div class="absolute inset-0 pointer-events-none">
        <div class="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-indigo-600/10 blur-[120px]"></div>
        <div class="absolute bottom-0 right-0 w-[300px] h-[300px] rounded-full bg-emerald-500/5 blur-[80px]"></div>
      </div>

      <div class="relative mb-8 inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full border border-indigo-500/30 bg-indigo-500/10 text-indigo-300 text-xs md:text-sm font-semibold uppercase tracking-widest reveal-in">
        <span class="inline-block w-2 h-2 rounded-full bg-indigo-400 animate-pulse" aria-hidden="true"></span>
        Merdeka Roll-In 2026
      </div>

      <h1 class="relative font-display text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-extrabold tracking-tight leading-[0.95] text-white mb-6 reveal-in" style="animation-delay: 0.1s">
        Merdeka<br/>
        <span class="bg-gradient-to-r from-indigo-400 via-purple-400 to-emerald-400 bg-clip-text text-transparent">Roll-In</span><br/>
        <span class="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-slate-300">2026</span>
      </h1>

      <p class="relative max-w-2xl text-slate-400 text-base md:text-lg leading-relaxed mt-6 mb-10 px-4 reveal-in" style="animation-delay: 0.2s">
        Private morning meet-up &amp; custom showcase untuk komunitas Vespa &amp; Custom Bike.<br/>
        <strong class="text-slate-200 font-semibold">Minggu, 16 Agustus 2026 · 08.00–12.00 WIB</strong><br/>
        Rokstation Bintaro, Tangerang Selatan
      </p>

      <!-- Countdown Header -->
      <div class="relative text-center reveal-in" style="animation-delay: 0.25s">
        <p class="text-xs text-slate-500 uppercase tracking-widest font-bold font-mono">Waktu Tersisa Menuju Roll-In</p>
      </div>

      <div class="relative mt-4 mb-16 grid grid-cols-4 gap-4 sm:gap-8 max-w-2xl w-full px-4 reveal-in" style="animation-delay: 0.3s" role="timer" aria-label="Countdown to event">
        <div v-for="unit in countdown" :key="unit.label" class="flex flex-col items-center gap-2">
          <span class="font-mono text-4xl sm:text-5xl md:text-6xl font-extrabold text-white tabular-nums" :aria-label="`${unit.value} ${unit.label}`">{{ unit.value }}</span>
          <span class="text-xs sm:text-sm text-slate-500 uppercase tracking-wider font-semibold" aria-hidden="true">{{ unit.label }}</span>
        </div>
      </div>

      <!-- PORTAL NAVIGATION CARD -->
      <div class="relative w-full max-w-2xl px-4 reveal-in" style="animation-delay: 0.35s">
        <!-- Participant Path -->
        <div class="landing-card p-8 text-left flex flex-col justify-between hover-lift border-indigo-500/20 bg-gradient-to-br from-indigo-950/20 to-slate-950">
          <div class="space-y-4">
            <span class="landing-chip bg-indigo-500/10 text-indigo-400 border-indigo-500/20">Peserta &amp; Komunitas</span>
            <h3 class="text-2xl font-extrabold text-white">Portal Peserta</h3>
            <p class="text-sm text-slate-400 leading-relaxed">
              Registrasi undangan eksklusif Anda, klaim <strong>Rameria Passport</strong> digital untuk check-in instan, dapatkan voucher kopi gratis, dan akses hasil dokumentasi riding profesional pasca-acara.
            </p>
          </div>
          <div class="mt-8">
            <router-link to="/participant" class="pwa-btn-primary w-full text-center">
              Masuk ke Portal Peserta
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- ── ABOUT THE EVENT ── -->
    <section class="max-w-6xl mx-auto px-6 sm:px-8 py-24 space-y-16">
      <div class="text-center space-y-6 reveal">
        <span class="landing-chip">Tentang Acara</span>
        <h2 class="text-4xl md:text-5xl font-extrabold text-white leading-tight">Kualitas, Bukan Kuantitas</h2>
        <p class="max-w-3xl mx-auto text-slate-400 text-base md:text-lg leading-relaxed">
          Kami sengaja tidak mengejar ribuan pengunjung acak - melainkan mengkurasi <strong class="text-slate-200">50 undangan terpilih</strong>
          dari skena Vespa &amp; Motor Custom di Jakarta Selatan dan Bintaro untuk berkumpul, berkendara, dan bertukar cerita.
        </p>
      </div>

      <div class="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
        <div v-for="(stat, i) in stats" :key="stat.label" class="landing-card p-6 lg:p-8 text-center space-y-3 reveal hover-lift" :style="`animation-delay: ${0.1 * i}s`">
          <span class="text-4xl md:text-5xl font-extrabold text-indigo-400 block leading-none">{{ stat.value }}</span>
          <span class="text-sm text-slate-400 font-semibold block">{{ stat.label }}</span>
        </div>
      </div>
    </section>

    <!-- ── FOOTER ── -->
    <footer class="border-t border-white/5 py-12 text-center space-y-3 px-6">
      <p class="text-base font-bold text-slate-300">Rameria</p>
      <p class="text-sm text-slate-600">&copy; 2026 Rameria. All Rights Reserved.</p>
    </footer>

  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'

// Target: 16 Agustus 2026, 08:00 WIB (UTC+7)
const targetDate = new Date('2026-08-16T01:00:00Z')

function calcCountdown() {
  const now = new Date()
  const diff = Math.max(0, targetDate.getTime() - now.getTime())
  const d = Math.floor(diff / (1000 * 60 * 60 * 24))
  const h = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  const s = Math.floor((diff % (1000 * 60)) / 1000)
  return [
    { value: String(d).padStart(2, '0'), label: 'Hari' },
    { value: String(h).padStart(2, '0'), label: 'Jam' },
    { value: String(m).padStart(2, '0'), label: 'Menit' },
    { value: String(s).padStart(2, '0'), label: 'Detik' },
  ]
}

const countdown = ref(calcCountdown())
let timer: any = null

onMounted(() => { 
  timer = setInterval(() => { countdown.value = calcCountdown() }, 1000) 
})

onUnmounted(() => { 
  clearInterval(timer) 
})

const stats = [
  { value: '50', label: 'Undangan Terkurasi' },
  { value: '4 Jam', label: 'Durasi Acara' },
  { value: '2 Lantai', label: 'Area Indoor Ber-AC' },
  { value: '3 Zona', label: 'Aktivasi Kemitraan' },
]
</script>

<style scoped>
.landing-wrapper {
  background-color: #05070f;
  background-image:
    linear-gradient(to right, rgba(255,255,255,0.015) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255,255,255,0.015) 1px, transparent 1px);
  background-size: 32px 32px;
  color: #f8fafc;
  font-family: var(--font-body);
}

.landing-card {
  background-color: rgba(11, 15, 29, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 1.25rem;
  backdrop-filter: blur(16px);
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.landing-card.hover-lift {
  cursor: default;
}

.landing-card.hover-lift:hover {
  transform: translateY(-4px);
  border-color: rgba(99, 102, 241, 0.2);
  box-shadow: 0 8px 32px rgba(99, 102, 241, 0.15);
}

.landing-chip {
  display: inline-block;
  padding: 0.25rem 0.875rem;
  border-radius: 9999px;
  border: 1px solid rgba(99, 102, 241, 0.3);
  background-color: rgba(99, 102, 241, 0.08);
  color: #a5b4fc;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

/* Premium gradient text */
.bg-gradient-to-r {
  background-size: 200% auto;
  animation: shimmer 3s linear infinite;
}

@keyframes shimmer {
  0%, 100% { background-position: 0% center; }
  50% { background-position: 100% center; }
}

/* Light mode overrides */
html.light .landing-wrapper {
  background-color: #f8fafc;
  color: #0f172a;
}

html.light .landing-card {
  background-color: rgba(255, 255, 255, 0.8);
  border-color: rgba(0, 0, 0, 0.08);
}

html.light .landing-chip {
  border-color: rgba(79, 70, 229, 0.3);
  background-color: rgba(79, 70, 229, 0.08);
  color: #4f46e5;
}

.reveal-in {
  opacity: 0;
  animation: fadeInUpLoad 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@keyframes fadeInUpLoad {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
